import dayjs from "dayjs";
import { ProductDuration } from "../../Models/OrderBookEntry";
import { Exchange } from "../ExchangeSelectorContextProvider";

// Define the type for contract identifier
export type ContractIdentifier = {
  delivery_area_id: string;
  contract_id: number;
};

export enum ProductType {
  Local = "Local",
}

export const productTypeToList = (): ProductType[] => {
  return [ProductType.Local];
};

// Define the type for the contract itself
export type Contract = {
  open_for_trading: boolean;
  area_short_name: string;

  contract_identifier: ContractIdentifier;
  product_name: string;
  delivery_start: string; // ISO 8601 date string
  delivery_end: string; // ISO 8601 date string
  name: string;
  long_name: string;
  /**
   * Venue-reported lifecycle of the contract itself, distinct from
   * {@link trading_phase}. Observed across the shared CONTRACTS stream:
   * `Active`, `Inactive`, `Standby`, `Hibernation` — JEPX has only ever sent
   * `Active`. Left as a bare `string` on purpose: the stream carries eight
   * exchanges, a narrow union would lie, and a throwing coercion here would
   * kill the whole consumer loop on the first unseen value.
   */
  state: string;
  /**
   * The session phase. Observed: `Continuous`, `Closed`, `Balancing`. In every
   * sample across every exchange this tracks {@link open_for_trading} exactly
   * (`Balancing` counting as open), so `open_for_trading` is the flag to branch
   * on and this one is informational.
   */
  trading_phase: string;
  /**
   * When {@link trading_phase} ends. A *future* value on a contract that is not
   * `open_for_trading` is the signature of a suspension rather than a gate
   * close — the venue is not going to reopen it, but it never reached its own
   * deadline either.
   */
  trading_phase_end: string | null; // Nullable in case it's null
  publish_time: string; // ISO 8601 date string

  duration_hours: number;
  product_duration: ProductDuration;
  product_type: ProductType;

  exchange: Exchange;
};

export const parse = (data: string, exchange: Exchange): Contract => {
  const item: Contract = JSON.parse(data);
  item.exchange = exchange;
  item.product_duration = durationToType(item.duration_hours);
  return item;
};

export const id = (contract: ContractIdentifier) => {
  return contract.contract_id;
};

export const compositeKey = (contract: ContractIdentifier) => {
  return `${contract.delivery_area_id}_${contract.contract_id}`;
};

const durationToType = (duration: number): ProductDuration => {
  switch ((duration * 100).toFixed(0)) {
    case "100": {
      return ProductDuration.H;
    }
    case "50": {
      return ProductDuration.HH;
    }
    case "25": {
      return ProductDuration.Q;
    }
  }
  return ProductDuration.Block;
};

export const contractExpired = (contract: Contract): boolean => {
  const now = dayjs();

  return now > dayjs(contract.trading_phase_end ?? contract.delivery_start);
};
